import React, { useState, useEffect, useRef } from 'react';
import Toolbar from './Toolbar';
import NoteViewMain from './MainPanel/NoteViewMain';
import styles from './MainPanel.module.css';
import DescriptionModal from './MainPanel/DescriptionModal';
import Modal from 'react-modal';
import { usePanelContext, PANEL_COMPONENTS } from './contexts/PanelContext';
import { useNotes } from './contexts/NotesContext';
import StickysViewMain from './StickysViewMain';
import ThreadsView from './ThreadsView';
import AITest from './AITest';
import AIViewMain from './MainPanel/AIViewMain';
import NoteEditorContainer from './MainPanel/NoteEditorContainer';
import NoteTitleBar from './NoteTitleBar';
import FormatBar from './FormatBar';

Modal.setAppElement('#root');

const MainPanel = ({ 
  isImageUploadExpanded,
  activeDock,
  setActiveDock,
  isRightPanelVisible,
  isLeftPanelVisible,
  toggleRightPanel,
  toggleLeftPanel,
  onToggleImageUpload,
  onSidebarContentChange,
  activeContent,
  onContainerSwap,
  stickies,
  addSticky,
  updateSticky,
  removeSticky,
  onOpenSettings,
  onImageUpload 
}) => {
  // Get notes context
  const { 
    selectedNoteId,
    deleteNote,
    createNote,
    updateNote,
    notes
  } = useNotes();

  const [uploadedImageId, setUploadedImageId] = useState(null);
  const [isDescriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const noteViewRef = useRef();
  const [isAITestOpen, setIsAITestOpen] = useState(false);
  const { mainPanelContent } = usePanelContext();

  const refreshGallery = async () => {
    console.log('Attempting gallery refresh...');
    if (noteViewRef.current && noteViewRef.current.refreshImages) {
      console.log('Calling refreshImages...');
      await noteViewRef.current.refreshImages();
      console.log('Gallery refresh complete');
    }
  };

  const handleImagePaste = async (e) => {
    // ... existing image paste handling
  };

  const handleDescriptionUpdate = async (description) => {
    // ... existing description update handling
  };

  const renderDock = () => {
    const { mainPanelContent, isSwapped, PANEL_COMPONENTS } = usePanelContext();
    
    const noteViewProps = {
      ref: noteViewRef,
      isImageUploadExpanded: isImageUploadExpanded,
      onToggleImageUpload: onToggleImageUpload,
      onImagePaste: handleImagePaste,
      onImageUpload: onImageUpload
    };
    
    // When panels are swapped, adjust rendering behavior
    if (isSwapped) {
      // If main panel should show threads (which normally belong in right panel)
      if (mainPanelContent === PANEL_COMPONENTS.RIGHT_PANEL.THREADS) {
        return <ThreadsView />;
      }
      
      // If main panel should show sticky notes
      if (mainPanelContent === PANEL_COMPONENTS.MAIN_PANEL.STICKY_NOTES) {
        return <StickysViewMain 
          selectedNoteId={selectedNoteId}
          stickies={stickies}
          updateSticky={updateSticky}
          removeSticky={removeSticky}
          addSticky={addSticky}
          isInMainPanel={true} // Flag to indicate this is rendered in main panel
          isRightPanelVisible={isRightPanelVisible}
        />;
      }
    }
    
    // Default/normal panel content rendering
    switch (mainPanelContent) {
      case PANEL_COMPONENTS.MAIN_PANEL.NOTE_VIEW:
        return <NoteEditorContainer {...noteViewProps} />;
      case PANEL_COMPONENTS.MAIN_PANEL.AI_VIEW:
        return <AIViewMain />;  
      case PANEL_COMPONENTS.MAIN_PANEL.STICKY_NOTES:
        return <StickysViewMain 
          selectedNoteId={selectedNoteId}
          stickies={stickies}
          updateSticky={updateSticky}
          removeSticky={removeSticky}
          addSticky={addSticky}
          isInMainPanel={true} // Flag to indicate this is rendered in main panel
          isRightPanelVisible={isRightPanelVisible}
        />;
      case PANEL_COMPONENTS.RIGHT_PANEL.THREADS:
        return <ThreadsView />;
      default:
        return <NoteEditorContainer {...noteViewProps} />;
    }
  };

  // Compute class name based on panel visibility
  const mainPanelClasses = `${styles.mainPanel} ${
    isLeftPanelVisible ? styles.withLeftPanel : styles.withoutLeftPanel
  } ${
    isRightPanelVisible ? styles.withRightPanel : styles.withoutRightPanel
  }`;

  return (
    <div className={mainPanelClasses}>
      <Toolbar 
        isRightPanelVisible={isRightPanelVisible}
        isLeftPanelVisible={isLeftPanelVisible}
        toggleRightPanel={toggleRightPanel}
        toggleLeftPanel={toggleLeftPanel}
        onDeleteNote={() => setDeleteModalOpen(true)}
        onAddNote={createNote}
        onToggleImageUpload={onToggleImageUpload}
        onSidebarContentChange={onSidebarContentChange}
        activeContent={activeContent}
        onContainerSwap={onContainerSwap}
        onOpenSettings={onOpenSettings}
        onOpenAITest={() => setIsAITestOpen(true)}
      />
      
      <NoteTitleBar />
      
      {mainPanelContent === PANEL_COMPONENTS.MAIN_PANEL.NOTE_VIEW && <FormatBar />}
      
      <div className={styles.mainDock}>
        <div className={`content-container ${styles.contentBg}`}>
          {renderDock()}
        </div>
      </div>
 
      <DescriptionModal
        isOpen={isDescriptionModalOpen}
        onClose={() => setDescriptionModalOpen(false)}
        onSave={handleDescriptionUpdate}
        defaultDescription="Untitled"
      />
 
      <Modal
        isOpen={isDeleteModalOpen}
        onRequestClose={() => setDeleteModalOpen(false)}
        className={styles.modal}
        overlayClassName={styles.modalOverlay}
      >
        <div className={styles.modalContent}>
          <h3 className={styles.modalTitle}>Delete Note?</h3>
          <p className={styles.modalText}>
            This will delete the note and all associated images and sticky notes.
          </p>
          <div className={styles.modalActions}>
            <button 
              className={styles.deleteButton}
              onClick={async () => {
                await deleteNote(selectedNoteId);
                setDeleteModalOpen(false);
              }}
            >
              Delete
            </button>
            <button 
              className={styles.cancelButton}
              onClick={() => setDeleteModalOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
      
      <AITest 
        isOpen={isAITestOpen}
        onClose={() => setIsAITestOpen(false)}
      />
    </div>
  );
};

export default MainPanel;